import type { SafetyCaseRecommendation } from '../../services/reportsApi';
import { resolveSafetyControlReviewFromReviewPayload } from '../reportContext/safetyControlReview';

const MEASUREMENT_RECOMMENDATION_TYPES = new Set([
  'measurement_followup',
  'measurement',
  'followup_measurement',
]);

export type ControlActionStatusUi = 'planned' | 'in_progress' | 'implemented' | 'needs_followup';

export type ControlSiteDecisionUi = 'accepted' | 'not_proceeding';

export type FollowUpSentenceKind =
  | 'insufficient'
  | 'data_available'
  | 'waiting'
  | 'monitoring';

const INSUFFICIENT_TOKENS = [
  'insufficient_data',
  'incomplete_data',
  'insufficient_observation_time',
  'accepted_insufficient',
];

function asText(value: unknown): string {
  return String(value || '').trim();
}

function normalize(value: unknown): string {
  return asText(value).toLowerCase();
}

export function recommendationDisplayText(recommendation?: SafetyCaseRecommendation | null): string {
  return asText(
    recommendation?.effective_recommendation_text
    || recommendation?.current_recommendation_text
    || recommendation?.original_recommendation_text
    || recommendation?.recommendation_text,
  );
}

export function isMeasurementRecommendation(recommendation?: SafetyCaseRecommendation | null): boolean {
  if (!recommendation) return false;
  const type = normalize(recommendation.recommendation_type);
  if (MEASUREMENT_RECOMMENDATION_TYPES.has(type)) return true;
  const text = recommendationDisplayText(recommendation).toLowerCase();
  return text.includes('repeat measurement') || text.includes('matched baseline and follow-up');
}

function sortRecommendations(recommendations: SafetyCaseRecommendation[]): SafetyCaseRecommendation[] {
  return [...recommendations].sort((left, right) => (
    asText(left.generated_at).localeCompare(asText(right.generated_at))
  ));
}

export function selectPrimaryControlRecommendation(
  recommendations: SafetyCaseRecommendation[] | null | undefined,
): SafetyCaseRecommendation | null {
  const ordered = sortRecommendations(Array.isArray(recommendations) ? recommendations : []);
  return ordered.find((recommendation) => !isMeasurementRecommendation(recommendation)) || null;
}

export function selectMeasurementGuidanceRecommendation(
  recommendations: SafetyCaseRecommendation[] | null | undefined,
): SafetyCaseRecommendation | null {
  const ordered = sortRecommendations(Array.isArray(recommendations) ? recommendations : []);
  return ordered.find((recommendation) => isMeasurementRecommendation(recommendation)) || null;
}

export function mapControlStatusToApi(status: ControlActionStatusUi): string {
  switch (status) {
    case 'in_progress':
      return 'started';
    case 'implemented':
      return 'validated';
    case 'needs_followup':
      return 'completed';
    case 'planned':
    default:
      return 'planned';
  }
}

export function mapControlStatusFromApi(status: unknown): ControlActionStatusUi {
  switch (normalize(status)) {
    case 'started':
    case 'in_progress':
      return 'in_progress';
    case 'validated':
      return 'implemented';
    case 'completed':
      return 'needs_followup';
    case 'planned':
    default:
      return 'planned';
  }
}

export function formatControlStatusLabel(status: ControlActionStatusUi): string {
  switch (status) {
    case 'in_progress':
      return 'In progress';
    case 'implemented':
      return 'Implemented';
    case 'needs_followup':
      return 'Needs follow-up';
    case 'planned':
    default:
      return 'Planned';
  }
}

export function mapSiteDecisionFromApi(state: unknown): ControlSiteDecisionUi {
  const key = normalize(state);
  if (['rejected', 'deferred', 'cancelled', 'waived', 'not_proceeding'].includes(key)) {
    return 'not_proceeding';
  }
  return 'accepted';
}

export function mapSiteDecisionToApi(decision: ControlSiteDecisionUi): 'accepted' | 'rejected' {
  return decision === 'not_proceeding' ? 'rejected' : 'accepted';
}

export type SplitControlNote = {
  operatorNote: string;
  review: Record<string, unknown> | null;
  rawJson: string | null;
};

function tryParseJson(text: string): unknown | null {
  if (!text.startsWith('{') && !text.startsWith('[')) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function looksLikeSafetyControlReview(value: unknown): boolean {
  const record = asRecord(value);
  if (!record) return false;
  if (asRecord(record.safety_control_review)) return true;
  return [
    'verification_status',
    'control_objective',
    'field_verification_questions',
    'primary_control_lens',
  ].some((key) => key in record);
}

export function splitControlNoteAndReview(raw: unknown): SplitControlNote {
  const text = asText(raw);
  if (!text) return { operatorNote: '', review: null, rawJson: null };
  const parsed = tryParseJson(text);
  if (!parsed || !looksLikeSafetyControlReview(parsed)) {
    return { operatorNote: text, review: null, rawJson: null };
  }
  const record = asRecord(parsed) || {};
  const nested = asRecord(record.safety_control_review);
  return {
    operatorNote: asText(record.operator_notes),
    review: nested || record,
    rawJson: text,
  };
}

export function operatorVisibleNote(raw: unknown): string {
  return splitControlNoteAndReview(raw).operatorNote;
}

export function operatorNoteForApi(raw: unknown): string | undefined {
  const text = asText(raw);
  if (!text || splitControlNoteAndReview(text).rawJson) return undefined;
  return text;
}

export function humanSafetyControlReviewStatus(review: unknown): string | null {
  const nested = asRecord(review)?.safety_control_review || review;
  if (!nested || typeof nested !== 'object') return null;
  const view = resolveSafetyControlReviewFromReviewPayload(nested as any);
  const label = asText(view?.verificationStatusLabel);
  return label || null;
}

export function resolveControlReviewStatusLabel(input: {
  review?: unknown;
  notes?: unknown;
}): string | null {
  return humanSafetyControlReviewStatus(input.review)
    || humanSafetyControlReviewStatus(splitControlNoteAndReview(input.notes).review);
}

export function resolveFollowUpSentence(input: {
  hasControl?: boolean;
  followupObservedCount?: unknown;
  directionalChange?: unknown;
  rateChangePercent?: unknown;
  measurementStatus?: unknown;
  measurementConfidence?: unknown;
}): { kind: FollowUpSentenceKind; sentence: string } {
  const tokens = [
    normalize(input.measurementStatus),
    normalize(input.measurementConfidence),
  ];
  if (tokens.some((token) => INSUFFICIENT_TOKENS.some((marker) => token.includes(marker)))) {
    return {
      kind: 'insufficient',
      sentence: 'More observations are needed before the effect of this control can be assessed.',
    };
  }
  const directional = normalize(input.directionalChange);
  const hasDirection = Boolean(directional) && !['not_measured', 'pending', 'unknown'].includes(directional);
  const hasCount = input.followupObservedCount != null && asText(input.followupObservedCount) !== '';
  const hasRate = input.rateChangePercent != null && Number.isFinite(Number(input.rateChangePercent));
  if (hasCount || hasDirection || hasRate) {
    return { kind: 'data_available', sentence: 'Follow-up data available.' };
  }
  if (input.hasControl) {
    return { kind: 'waiting', sentence: 'Waiting for more post-action observations.' };
  }
  return { kind: 'monitoring', sentence: 'Monitoring continues.' };
}
